import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  FileText, Loader2, Copy, Check, RefreshCw, BookOpen,
  ChevronDown, ChevronUp, Sparkles
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { API } from "../../lib/api";

const LENGTH_CONFIG = {
  one_sentence: { label: "One Sentence", description: "~30 words", icon: "✏️" },
  short: { label: "Short", description: "2-3 sentences", icon: "📝" },
  medium: { label: "Medium", description: "Paragraph", icon: "📄" },
  long: { label: "Long", description: "Detailed", icon: "📃" },
  full_page: { label: "Full Page", description: "500 words", icon: "📖" }
};

const SynopsisLadderModal = ({ 
  open, 
  onClose, 
  projectId,
  projectTitle,
  chapters = [],
  currentChapter = 1
}) => {
  const [mode, setMode] = useState("chapter"); // chapter, book, ladder
  const [selectedChapter, setSelectedChapter] = useState(currentChapter);
  const [selectedLength, setSelectedLength] = useState("medium");
  
  const [generating, setGenerating] = useState(false);
  const [synopsis, setSynopsis] = useState(null);
  
  const [ladderJob, setLadderJob] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedChapter(currentChapter);
      setSynopsis(null);
      setLadderJob(null);
    }
  }, [open, currentChapter]);

  const getChapterContent = (chapterNum) => {
    const chapter = chapters.find(c => c.chapter_number === chapterNum);
    return chapter?.content || "";
  };

  // Generate single synopsis
  const handleGenerateSynopsis = async () => {
    setGenerating(true);
    setSynopsis(null);

    try {
      if (mode === "chapter") {
        const content = getChapterContent(selectedChapter);
        if (!content || content.length < 100) {
          toast.error("Chapter content too short");
          setGenerating(false);
          return;
        }

        const res = await axios.post(`${API}/synopsis/chapter`, {
          project_id: projectId,
          chapter_number: selectedChapter,
          content,
          length: selectedLength
        });
        setSynopsis(res.data);
      } else {
        const chaptersData = chapters
          .filter(c => c.content && c.content.length > 100)
          .map(c => ({
            chapter_number: c.chapter_number,
            title: c.title,
            content: c.content
          }));

        if (chaptersData.length === 0) {
          toast.error("No chapters with content");
          setGenerating(false);
          return;
        }

        const res = await axios.post(`${API}/synopsis/book`, {
          project_id: projectId,
          title: projectTitle,
          chapters: chaptersData,
          length: selectedLength
        });
        setSynopsis(res.data);
      }
      toast.success("Synopsis generated!");
    } catch (error) {
      toast.error("Generation failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  // Generate full ladder
  const handleGenerateLadder = async () => {
    setGenerating(true);
    setLadderJob(null);

    try {
      let content = "";
      if (mode === "chapter") {
        content = getChapterContent(selectedChapter);
      } else {
        content = chapters
          .filter(c => c.content)
          .map(c => `CHAPTER ${c.chapter_number}: ${c.title || ""}\n${c.content?.slice(0, 1500)}...`)
          .join("\n\n");
      }

      if (!content || content.length < 100) {
        toast.error("Not enough content to generate synopsis");
        setGenerating(false);
        return;
      }

      const res = await axios.post(`${API}/synopsis/ladder`, {
        project_id: projectId,
        content,
        content_type: mode === "chapter" ? "chapter" : "book",
        title: mode === "book" ? projectTitle : `Chapter ${selectedChapter}`
      });

      setLadderJob({ id: res.data.job_id, status: "pending", progress: 0 });
      pollLadderJob(res.data.job_id);
    } catch (error) {
      setGenerating(false);
      toast.error("Failed to start ladder generation");
    }
  };

  const pollLadderJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/synopsis/ladder/jobs/${jobId}`);
      setLadderJob(res.data);

      if (res.data.status === "completed") {
        setGenerating(false);
        toast.success("Synopsis ladder complete!");
      } else if (res.data.status === "failed") {
        setGenerating(false);
        toast.error("Ladder generation failed");
      } else {
        setTimeout(() => pollLadderJob(jobId), 2000);
      }
    } catch (error) {
      setGenerating(false);
      toast.error("Failed to check job status");
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="w-5 h-5 text-cyan-400" />
            Synopsis Ladder
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generate summaries at multiple lengths — from one sentence to full page
          </DialogDescription>
        </DialogHeader>

        {/* Mode Tabs */}
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger value="chapter" className="data-[state=active]:bg-cyan-600">
              Chapter Synopsis
            </TabsTrigger>
            <TabsTrigger value="book" className="data-[state=active]:bg-cyan-600">
              Book Synopsis
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="overflow-y-auto max-h-[55vh] space-y-4 pr-2">
          {/* Controls */}
          <div className="flex flex-wrap items-end gap-4">
            {mode === "chapter" && (
              <div>
                <label className="text-sm text-slate-400 block mb-1">Chapter:</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(parseInt(e.target.value))}
                  className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
                >
                  {chapters.map(c => (
                    <option key={c.chapter_number} value={c.chapter_number}>
                      Ch {c.chapter_number}: {c.title?.slice(0, 30) || "Untitled"}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="text-sm text-slate-400 block mb-1">Length:</label>
              <select
                value={selectedLength}
                onChange={(e) => setSelectedLength(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm"
              >
                {Object.entries(LENGTH_CONFIG).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.icon} {config.label} ({config.description})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleGenerateSynopsis}
              disabled={generating}
              className="flex-1 bg-cyan-600 hover:bg-cyan-500"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Generate Synopsis</>
              )}
            </Button>
            <Button
              onClick={handleGenerateLadder}
              disabled={generating}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <FileText className="w-4 h-4 mr-2" />
              Full Ladder
            </Button>
          </div>

          {/* Single Synopsis Result */}
          {synopsis && !ladderJob && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/50 rounded-xl p-4 border border-slate-700"
            >
              <div className="flex items-center justify-between mb-2">
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                  {LENGTH_CONFIG[selectedLength]?.label || selectedLength}
                </Badge>
                <span className="text-xs text-slate-500">{synopsis.word_count} words</span>
              </div>
              <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {synopsis.synopsis}
              </p>
              <Button
                onClick={() => copyToClipboard(synopsis.synopsis, "single")}
                variant="ghost"
                size="sm"
                className="mt-3 text-slate-400 hover:text-white"
              >
                {copied === "single" ? (
                  <><Check className="w-4 h-4 mr-1 text-emerald-400" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-1" /> Copy</>
                )}
              </Button>
            </motion.div>
          )}

          {/* Ladder Results */}
          {ladderJob && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-3"
            >
              {ladderJob.status !== "completed" && (
                <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-300">Generating all lengths...</span>
                    <Badge className="bg-cyan-500/20 text-cyan-400">{ladderJob.status}</Badge>
                  </div>
                  <Progress value={ladderJob.progress} className="h-2 bg-slate-700" />
                </div>
              )}

              {ladderJob.status === "completed" && ladderJob.ladder && (
                <div className="space-y-3">
                  {Object.entries(ladderJob.ladder).map(([length, data]) => (
                    <div
                      key={length}
                      className="bg-slate-800/30 rounded-xl border border-slate-700/50 overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 border-b border-slate-700/50">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{LENGTH_CONFIG[length]?.icon}</span>
                          <span className="font-medium text-white">{LENGTH_CONFIG[length]?.label}</span>
                          <span className="text-xs text-slate-500">({data.word_count} words)</span>
                        </div>
                        <Button
                          onClick={() => copyToClipboard(data.synopsis, length)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-white h-7"
                        >
                          {copied === length ? (
                            <Check className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                      <div className="p-4">
                        {data.error ? (
                          <p className="text-red-400 text-sm">{data.error}</p>
                        ) : (
                          <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                            {data.synopsis}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SynopsisLadderModal;
